import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Search, ArrowDown, ArrowUp, ExternalLink } from 'lucide-react';
import SortFilterControl from '../../../components/ui/SortFilterControl';
import { loadMarketplaceSecurities } from '../lib/marketplaceApi';
import {
  loadMarketplaceSort,
  saveMarketplaceSort,
  MARKETPLACE_SORT_FIELDS,
} from '../lib/marketplaceSortPref';

const NAVY = '#0A0A0A';
const NAVY_SOFT = '#141414';
const CREAM = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';
const CHANGE_UP = '#16a34a';
const CHANGE_DOWN = '#dc2626';

// Sectypes EMMA can resolve: municipal bonds and variable-rate munis. EMMA
// only covers municipal securities - an equity or Treasury CUSIP would land
// on an empty page, so those rows simply have no trades link.
const MUNI_SECTYPES = new Set(['mb', 'vm']);

// EMMA User Agreement linking conditions (see
// docs/MARKETPLACE_TRADE_DATA_OPTIONS_2026-08-15.md): identify the link as
// the MSRB website, no MSRB logos, open their site unmodified. A popup
// window satisfies "unmodified"; the accessible name carries the required
// identification.
const EMMA_LINK_NAME =
  'View recent trades on the Municipal Securities Rulemaking Board website (EMMA)';
const emmaUrl = (cusip) =>
  `https://emma.msrb.org/Security/Details/?id=${encodeURIComponent(cusip)}`;

const MV_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const formatMarketValue = (v) => (v !== 0 ? MV_FORMAT.format(v) : '–');
const formatSigned = (v) => `${v >= 0 ? '+' : '-'}${MV_FORMAT.format(Math.abs(v))}`;
// "YYYY-MM-DD" -> "MM/DD/YYYY"; the em-dash marks a position with no
// purchase in the ledger (e.g. transferred in, never bought).
const formatDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '–';
};

const SORT_FIELD_OPTIONS = [
  { key: MARKETPLACE_SORT_FIELDS.TRADE_DATE, label: 'Trade date' },
  { key: MARKETPLACE_SORT_FIELDS.MARKET_VALUE, label: 'Market value' },
  { key: MARKETPLACE_SORT_FIELDS.ACTIVITY, label: 'Activity' },
];
// Which column header carries the sort glyph for each dimension.
const SORT_COLUMN = {
  [MARKETPLACE_SORT_FIELDS.TRADE_DATE]: 'Last Purchase',
  [MARKETPLACE_SORT_FIELDS.MARKET_VALUE]: 'Market Value',
  [MARKETPLACE_SORT_FIELDS.ACTIVITY]: 'Day Change',
};
// Header click -> sort dimension (the inverse map).
const COLUMN_FIELD = Object.fromEntries(
  Object.entries(SORT_COLUMN).map(([f, col]) => [col, f])
);

const COLUMNS = [
  'Security', 'CUSIP', 'Ticker', 'Last Purchase', 'Market Value', 'Day Change', 'Trades',
];
const RIGHT_ALIGN_COLS = new Set(['Market Value', 'Day Change']);

const MarketPlacePage = () => {
  const { darkMode } = useTheme();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Seeded from localStorage so the user's last pick survives sessions.
  const [sortConfig, setSortConfig] = useState(() => loadMarketplaceSort());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadMarketplaceSecurities();
        if (!cancelled) setPayload(data);
      } catch (e) {
        if (!cancelled) setError('Failed to load securities');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applySort = (next) => {
    setSortConfig(next);
    saveMarketplaceSort(next);
  };

  const textPrimary = darkMode ? CREAM : NAVY;
  const textMuted = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';
  const hairline = darkMode ? HAIRLINE : 'rgba(0,0,0,0.10)';
  const hairlineStrong = darkMode ? HAIRLINE_STRONG : 'rgba(0,0,0,0.22)';
  const rowHover = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const tableHeaderBg = darkMode ? NAVY_SOFT : 'rgba(10,23,51,0.06)';

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const rows = (payload?.securities || []).filter((s) => {
      if (!q) return true;
      return [s.cusip, s.ticker, s.symbol, s.name].some(
        (v) => v && v.toLowerCase().includes(q)
      );
    });
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    // Rows missing the sorted value sink to the bottom in BOTH directions
    // (same convention as the Portfolios tab's inception sort).
    const nullsLast = (a, b, cmp) => {
      if (a == null) return b == null ? 0 : 1;
      if (b == null) return -1;
      return cmp() * dir;
    };
    const compare = {
      [MARKETPLACE_SORT_FIELDS.TRADE_DATE]: (a, b) =>
        nullsLast(a.last_purchase_date, b.last_purchase_date, () =>
          a.last_purchase_date.localeCompare(b.last_purchase_date)),
      [MARKETPLACE_SORT_FIELDS.MARKET_VALUE]: (a, b) =>
        (a.market_value - b.market_value) * dir,
      [MARKETPLACE_SORT_FIELDS.ACTIVITY]: (a, b) =>
        nullsLast(a.activity, b.activity, () => a.activity - b.activity),
    }[sortConfig.field];
    return [...rows].sort(
      (a, b) => compare(a, b) || (a.name || '').localeCompare(b.name || '')
    );
  }, [payload, searchQuery, sortConfig]);

  const activeSortCol = SORT_COLUMN[sortConfig.field];

  const onHeaderClick = (col) => {
    const field = COLUMN_FIELD[col];
    if (!field) return;
    applySort(
      field === sortConfig.field
        ? { field, dir: sortConfig.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'desc' }
    );
  };

  const openEmma = (cusip) => {
    window.open(
      emmaUrl(cusip),
      'emma_trades',
      'noopener,noreferrer,popup=yes,width=1100,height=850'
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: 24,
        boxSizing: 'border-box',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1
          className="font-display"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: textPrimary,
            margin: 0,
          }}
        >
          Market Place
        </h1>
        {payload?.as_of && (
          <div style={{ marginTop: 6, fontSize: 12, color: textMuted }}>
            Holdings as of {payload.as_of}
            {payload.prev_as_of ? ` · day change vs ${payload.prev_as_of}` : ''}
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <Search
            size={15}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: textMuted,
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            placeholder="Search by CUSIP, ticker, or name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: 36,
              paddingRight: 14,
              paddingTop: 8,
              paddingBottom: 8,
              borderRadius: 6,
              border: `1px solid ${hairlineStrong}`,
              background: darkMode ? NAVY_SOFT : 'rgba(255,255,255,0.7)',
              color: textPrimary,
              fontSize: 14,
              fontFamily: 'Inter, sans-serif',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <SortFilterControl
          value={sortConfig}
          onChange={applySort}
          fields={SORT_FIELD_OPTIONS}
          darkMode={darkMode}
        />
      </div>

      {/* EMMA attribution (linking condition: identify the destination). */}
      <div style={{ marginBottom: 12, fontSize: 11, color: textMuted }}>
        Trade links open the Municipal Securities Rulemaking Board website
        (EMMA) in a new window.
      </div>

      {/* Securities table (scrollable) */}
      <div
        className="custom-scrollbar"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          borderRadius: 4,
          border: `1px solid ${hairline}`,
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const isNumeric = RIGHT_ALIGN_COLS.has(col);
                const isSortCol = col === activeSortCol;
                const sortable = !!COLUMN_FIELD[col];
                return (
                  <th
                    key={col}
                    onClick={sortable ? () => onHeaderClick(col) : undefined}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: tableHeaderBg,
                      padding: '10px 20px',
                      textAlign: isNumeric ? 'right' : 'left',
                      fontSize: 11,
                      fontWeight: 500,
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.18em',
                      color: textMuted,
                      whiteSpace: 'nowrap',
                      cursor: sortable ? 'pointer' : 'default',
                    }}
                  >
                    {isSortCol ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent: isNumeric ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {col}{' '}
                        {sortConfig.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                      </span>
                    ) : col}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  Loading securities…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={COLUMNS.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  {error}
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  {searchQuery ? 'No securities match your search' : 'No securities found'}
                </td>
              </tr>
            ) : (
              visible.map((s) => {
                const change =
                  s.prev_market_value != null ? s.market_value - s.prev_market_value : null;
                const up = change != null && change > 0;
                const down = change != null && change < 0;
                const changeColor = up ? CHANGE_UP : down ? CHANGE_DOWN : textMuted;
                const isMuni = MUNI_SECTYPES.has(s.sectype) && !!s.cusip;
                return (
                  <tr
                    key={s.security_id}
                    style={{ borderTop: `1px solid ${hairline}`, transition: 'background 120ms' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = rowHover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td
                      style={{ padding: '12px 20px', fontSize: 14, fontWeight: 500, color: textPrimary, maxWidth: 340 }}
                      title={s.name || undefined}
                    >
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.name || '–'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                      {s.cusip || '–'}
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                      {s.ticker || '–'}
                    </td>
                    <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                      {formatDate(s.last_purchase_date)}
                    </td>
                    <td className="tabular-nums" style={{ padding: '12px 20px', fontSize: 14, color: textPrimary, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {formatMarketValue(s.market_value)}
                    </td>
                    <td
                      className="tabular-nums"
                      style={{ padding: '12px 20px', fontSize: 14, whiteSpace: 'nowrap', textAlign: 'right', color: changeColor }}
                      title={
                        change != null && payload?.prev_as_of
                          ? `${payload.prev_as_of} -> ${payload.as_of}`
                          : undefined
                      }
                    >
                      {change != null ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {formatSigned(change)}
                          {up && <ArrowUp size={11} />}
                          {down && <ArrowDown size={11} />}
                        </span>
                      ) : '–'}
                    </td>
                    <td style={{ padding: '12px 20px', whiteSpace: 'nowrap' }}>
                      {isMuni ? (
                        <button
                          type="button"
                          aria-label={EMMA_LINK_NAME}
                          title={EMMA_LINK_NAME}
                          onClick={() => openEmma(s.cusip)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '5px 10px',
                            borderRadius: 6,
                            border: `1px solid ${hairlineStrong}`,
                            background: 'transparent',
                            color: textMuted,
                            fontSize: 12,
                            fontFamily: 'Inter, sans-serif',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = textPrimary; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = textMuted; }}
                        >
                          View trades
                          <ExternalLink size={12} />
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: textMuted }}>–</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MarketPlacePage;
